import { OrderDto } from '../../dto/order';
import { SupplierOrderItemAllocationDto, SupplierOrderItemDto } from '../../dto/supplierOrder';
import { OrderProduct } from '../../types/order';
import { convertAllocationQuantity } from '../../utils/dimensionConversion';
import type { SupplierOrderAllocationUnit } from './api/supplierOrdersApi';

export type SupplierOrderPurchaseMode = 'CUSTOMER_ORDER' | 'STOCK';
export type SupplierOrderSurplusDisposition = 'STOCK' | 'CUSTOMER' | 'WASTE';

export interface CustomerLinkedSupplierItemDraft {
  sourceOrderProductId: string;
  productVariantId?: string | null;
  name: string;
  quantity: number;
  unit: SupplierOrderAllocationUnit;
  customerQuantity: number;
  customerUnit: SupplierOrderAllocationUnit;
  surplusDisposition: SupplierOrderSurplusDisposition;
  width?: number | null;
  length?: number | null;
  height?: number | null;
  thickness?: number | null;
  productCategoryName?: string | null;
  productCategoryColor?: string | null;
  productCategoryDimensionDisplayProfile?: string | null;
  currency?: string;
  unitCost?: number;
}

export interface ExistingSupplierItemAllocationDraft extends CustomerLinkedSupplierItemDraft {
  orderProductId: string;
  supplierOrderItemId: string;
  customerProductName: string;
  supplierItemName: string;
}

/**
 * Seed the allocation form from a link that is already saved, so correcting
 * "226 -> 208" starts from 226 rather than from a fresh match against the
 * customer order (owner decision 2026-09-21,
 * `docs/decisions/2026-09-21-supplier-order-link-warning.md`). The ZD's own
 * lines supply the supplier-side quantity, unit and dimensions the balance
 * preview needs; the persisted allocation supplies the customer side. An
 * allocation whose supplier line no longer exists is dropped — the operator
 * cannot re-target it from this form.
 */
export function buildSupplierOrderLinkEditAllocations(
  link: { itemAllocations?: SupplierOrderItemAllocationDto[] },
  supplierItems: SupplierOrderItemDto[],
): ExistingSupplierItemAllocationDraft[] {
  return (link.itemAllocations || []).flatMap((allocation) => {
    const supplierItem = supplierItems.find((item) => item.id === allocation.supplierOrderItemId);
    if (!supplierItem) return [];
    return [{
      sourceOrderProductId: allocation.orderProductId,
      orderProductId: allocation.orderProductId,
      supplierOrderItemId: supplierItem.id,
      productVariantId: allocation.productVariantId || supplierItem.productVariantId || null,
      name: supplierItem.name,
      customerProductName: allocation.orderProductName || supplierItem.name,
      supplierItemName: supplierItem.name,
      quantity: Number(supplierItem.quantity || 0),
      unit: asAllocationUnit(supplierItem.unit),
      customerQuantity: Number(allocation.customerQuantity || 0),
      customerUnit: asAllocationUnit(allocation.customerUnit),
      surplusDisposition: allocation.surplusDisposition || 'STOCK',
      width: supplierItem.width ?? null,
      length: supplierItem.length ?? null,
      height: supplierItem.height ?? null,
      thickness: supplierItem.thickness ?? null,
    }];
  });
}

export interface ExistingSupplierItemAllocationMatch {
  allocations: ExistingSupplierItemAllocationDraft[];
  unmatchedCustomerProductNames: string[];
}

export interface PreviousCustomerProductAllocation {
  orderProductId: string;
  customerQuantity: number;
  customerUnit: string;
}

const asAllocationUnit = (value?: string | null): SupplierOrderAllocationUnit => (
  value === 'm2' || value === 'm3' || value === 'pcs' ? value : 'pcs'
);

const customerQuantityForProduct = (product: OrderProduct): number => (
  Number(product.saleQuantity ?? product.quantity ?? 0)
);

export function buildSupplierItemsFromCustomerOrder(
  order: Pick<OrderDto, 'products'>,
  canEditSupplierPurchasePrices: boolean,
  previousAllocations: PreviousCustomerProductAllocation[] = [],
): CustomerLinkedSupplierItemDraft[] {
  return (order.products || []).map((product) => {
    const customerUnit = asAllocationUnit(product.saleUnit || product.unit);
    const alreadyAllocated = previousAllocations
      .filter((allocation) => (
        allocation.orderProductId === product.id
        && asAllocationUnit(allocation.customerUnit) === customerUnit
      ))
      .reduce((sum, allocation) => sum + Number(allocation.customerQuantity || 0), 0);
    const customerQuantity = Math.max(customerQuantityForProduct(product) - alreadyAllocated, 0);

    return {
      sourceOrderProductId: product.id,
      productVariantId: product.productVariantId || null,
      name: product.name,
      quantity: customerQuantity,
      unit: customerUnit,
      customerQuantity,
      customerUnit,
      surplusDisposition: 'STOCK' as const,
      width: product.width ?? null,
      length: product.length ?? null,
      height: product.height ?? null,
      thickness: product.thickness ?? null,
      productCategoryName: product.productCategoryName ?? null,
      productCategoryColor: product.productCategoryColor ?? null,
      productCategoryDimensionDisplayProfile: product.productCategoryDimensionDisplayProfile ?? null,
      ...(canEditSupplierPurchasePrices ? { currency: 'PLN', unitCost: 0 } : {}),
    };
  }).filter((item) => item.customerQuantity > 0);
}

const normalizedProductName = (value?: string | null): string => (
  String(value || '').trim().toLocaleLowerCase('pl-PL')
);

export function buildExistingSupplierOrderItemAllocations(
  order: Pick<OrderDto, 'products'>,
  supplierItems: SupplierOrderItemDto[],
): ExistingSupplierItemAllocationMatch {
  const usedSupplierItemIds = new Set<string>();
  const allocations: ExistingSupplierItemAllocationDraft[] = [];
  const unmatchedCustomerProductNames: string[] = [];

  for (const product of order.products || []) {
    const matchingSupplierItem = supplierItems.find((item) => {
      if (usedSupplierItemIds.has(item.id)) return false;
      if (product.productVariantId || item.productVariantId) {
        return Boolean(
          product.productVariantId
          && item.productVariantId
          && product.productVariantId === item.productVariantId
        );
      }
      return Boolean(
        normalizedProductName(product.name)
        && normalizedProductName(product.name) === normalizedProductName(item.name)
      );
    });

    if (!matchingSupplierItem) {
      unmatchedCustomerProductNames.push(product.catalogProductName || product.name);
      continue;
    }

    usedSupplierItemIds.add(matchingSupplierItem.id);
    const customerUnit = asAllocationUnit(product.saleUnit || product.unit);
    allocations.push({
      sourceOrderProductId: product.id,
      orderProductId: product.id,
      supplierOrderItemId: matchingSupplierItem.id,
      productVariantId: product.productVariantId || null,
      name: matchingSupplierItem.name,
      customerProductName: product.catalogProductName || product.name,
      supplierItemName: matchingSupplierItem.name,
      quantity: Number(matchingSupplierItem.quantity || 0),
      unit: asAllocationUnit(matchingSupplierItem.unit),
      customerQuantity: customerQuantityForProduct(product),
      customerUnit,
      surplusDisposition: 'STOCK',
      width: matchingSupplierItem.width ?? product.width ?? null,
      length: matchingSupplierItem.length ?? product.length ?? null,
      height: matchingSupplierItem.height ?? product.height ?? null,
      thickness: matchingSupplierItem.thickness ?? product.thickness ?? null,
    });
  }

  return { allocations, unmatchedCustomerProductNames };
}

export interface SupplierItemAllocationBalance {
  supplierEquivalentInCustomerUnit: number | null;
  customerCoveredQuantity: number | null;
  surplusInCustomerUnit: number | null;
  allocatedSupplierQuantity: number | null;
  error: string | null;
}

export function calculateSupplierItemAllocationBalance(
  item: Partial<CustomerLinkedSupplierItemDraft>,
): SupplierItemAllocationBalance {
  const supplierQuantity = Number(item.quantity || 0);
  const customerQuantity = Number(item.customerQuantity || 0);
  const supplierUnit = asAllocationUnit(item.unit);
  const customerUnit = asAllocationUnit(item.customerUnit);
  const dimensions = {
    width: item.width,
    length: item.length,
    height: item.height,
    thickness: item.thickness,
  };
  const supplierToCustomer = convertAllocationQuantity({
    quantity: supplierQuantity,
    sourceUnit: supplierUnit,
    targetUnit: customerUnit,
    ...dimensions,
  });
  const customerToSupplier = convertAllocationQuantity({
    quantity: customerQuantity,
    sourceUnit: customerUnit,
    targetUnit: supplierUnit,
    ...dimensions,
  });

  if (supplierToCustomer.error || customerToSupplier.error) {
    return {
      supplierEquivalentInCustomerUnit: null,
      customerCoveredQuantity: null,
      surplusInCustomerUnit: null,
      allocatedSupplierQuantity: null,
      error: supplierToCustomer.error || customerToSupplier.error || 'UNSUPPORTED_CONVERSION',
    };
  }

  const supplierEquivalent = Number(supplierToCustomer.value || 0);
  const requiredSupplierQuantity = Number(customerToSupplier.value || 0);
  const surplus = Math.max(supplierEquivalent - customerQuantity, 0);
  const allocateWholeSupplierLine = surplus > 0 && item.surplusDisposition !== 'STOCK';

  return {
    supplierEquivalentInCustomerUnit: supplierEquivalent,
    customerCoveredQuantity: Math.min(supplierEquivalent, customerQuantity),
    surplusInCustomerUnit: surplus,
    allocatedSupplierQuantity: allocateWholeSupplierLine
      ? supplierQuantity
      : Math.min(requiredSupplierQuantity, supplierQuantity),
    error: null,
  };
}

export function findInvalidSupplierItemAllocationIndex(
  items: Array<Partial<CustomerLinkedSupplierItemDraft>>,
): number {
  return items.findIndex((item) => Boolean(calculateSupplierItemAllocationBalance(item).error));
}

export function changeSupplierItemUnit(
  item: Partial<CustomerLinkedSupplierItemDraft>,
  nextUnit: SupplierOrderAllocationUnit,
): number | null {
  const currentQuantity = Number(item.quantity || 0);
  const currentUnit = asAllocationUnit(item.unit);
  const conversion = convertAllocationQuantity({
    quantity: currentQuantity,
    sourceUnit: currentUnit,
    targetUnit: nextUnit,
    width: item.width,
    length: item.length,
    height: item.height,
    thickness: item.thickness,
  });

  return conversion.error || conversion.value == null ? null : conversion.value;
}
